
// Function to calculate age based on birthday
import { Language } from "@/contexts/language";

export const calculateAge = (birthday: Date, language: Language = 'en'): string => {
  const birthDate = new Date(birthday);
  const today = new Date();

  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();

  if (months < 0 || (months === 0 && days < 0)) {
    years--;
    months += 12;
  }

  if (days < 0) {
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    days += lastDayOfMonth;
    months--;
  }

  // Translations for time units
  const timeUnits = {
    year: {
      en: 'year',
      ja: '年',
      es: 'año',
      zh: '年'
    },
    years: {
      en: 'years',
      ja: '年',
      es: 'años',
      zh: '年'
    },
    month: {
      en: 'month',
      ja: 'ヶ月',
      es: 'mes',
      zh: '个月'
    },
    months: {
      en: 'months',
      ja: 'ヶ月',
      es: 'meses',
      zh: '个月'
    },
    week: {
      en: 'week',
      ja: '週間',
      es: 'semana',
      zh: '周'
    },
    weeks: {
      en: 'weeks',
      ja: '週間',
      es: 'semanas',
      zh: '周'
    },
    day: {
      en: 'day',
      ja: '日',
      es: 'día',
      zh: '天'
    },
    days: {
      en: 'days',
      ja: '日',
      es: 'días',
      zh: '天'
    },
    lessThanDay: {
      en: 'Less than a day',
      ja: '1日未満',
      es: 'Menos de un día',
      zh: '不到一天'
    }
  };

  console.log(`Formatting age for language: ${language} - Years: ${years}, Months: ${months}, Days: ${days}`);

  // Format age based on language
  if (years > 0) {
    const yearUnit = years > 1 ? timeUnits.years[language] : timeUnits.year[language];
    const monthUnit = months > 1 ? timeUnits.months[language] : timeUnits.month[language];
    
    if (language === 'en') {
      return `${years} ${yearUnit} ${months} ${monthUnit}`;
    } else if (language === 'ja') {
      return `${years}${yearUnit}${months}${monthUnit}`;
    } else if (language === 'zh') {
      return `${years}${yearUnit}${months}${monthUnit}`;
    } else { // es and fallback
      return `${years} ${yearUnit} ${months} ${monthUnit}`;
    }
  } else if (months > 0) {
    const monthUnit = months > 1 ? timeUnits.months[language] : timeUnits.month[language];
    const dayUnit = days > 1 ? timeUnits.days[language] : timeUnits.day[language];
    
    if (language === 'en') {
      return `${months} ${monthUnit} ${days} ${dayUnit}`;
    } else if (language === 'ja') {
      return `${months}${monthUnit}${days}${dayUnit}`;
    } else if (language === 'zh') {
      return `${months}${monthUnit}${days}${dayUnit}`;
    } else { // es and fallback
      return `${months} ${monthUnit} ${days} ${dayUnit}`;
    }
  } else if (days > 0) {
    const diffInWeeks = Math.floor(days / 7);
    const remainingDays = days % 7;

    if (diffInWeeks > 0) {
      const weekUnit = diffInWeeks > 1 ? timeUnits.weeks[language] : timeUnits.week[language];
      const dayUnit = remainingDays > 1 ? timeUnits.days[language] : timeUnits.day[language];
      
      if (language === 'en') {
        return `${diffInWeeks} ${weekUnit} ${remainingDays} ${dayUnit}`;
      } else if (language === 'ja') {
        return `${diffInWeeks}${weekUnit}${remainingDays}${dayUnit}`;
      } else if (language === 'zh') {
        return `${diffInWeeks}${weekUnit}${remainingDays}${dayUnit}`;
      } else { // es and fallback
        return `${diffInWeeks} ${weekUnit} ${remainingDays} ${dayUnit}`;
      }
    } else {
      const dayUnit = days > 1 ? timeUnits.days[language] : timeUnits.day[language];
      
      if (language === 'en') {
        return `${days} ${dayUnit}`;
      } else if (language === 'ja' || language === 'zh') {
        return `${days}${dayUnit}`;
      } else { // es and fallback
        return `${days} ${dayUnit}`;
      }
    }
  } else {
    return timeUnits.lessThanDay[language];
  }
};

// Helper to check if baby is under 3 years old
export const isBabyUnderThreeYears = (birthday: string | Date): boolean => {
  const birthDate = new Date(birthday);
  const ageString = calculateAge(birthDate);
  
  // Check if less than 3 years old
  const years = parseInt(ageString.split(' ')[0]);
  return ageString.includes('month') || 
         ageString.includes('week') || 
         ageString.includes('day') || 
         (years < 3);
};

// Function to calculate the current age in months
export const calculateAgeInMonths = (birthday: Date): number => {
  const birthDate = new Date(birthday);
  const today = new Date();

  let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
  months -= birthDate.getMonth();
  months += today.getMonth();

  return months <= 0 ? 0 : months;
};
